import { Check } from 'lucide-react';

import { cn } from '@/lib/utils';

export type WizardStep = 'imaging' | 'patient-details';

const STEP_ORDER: readonly WizardStep[] = ['imaging', 'patient-details'] as const;

const STEP_LABELS: Record<WizardStep, string> = {
    imaging: 'Imaging',
    'patient-details': 'Confirm & create',
};

interface WizardStepIndicatorProps {
    current: WizardStep;
    /**
     * Dense variant sized for the shell's h-14 top bar (smaller
     * circles, tighter gaps, shorter connectors). The default variant
     * remains for full-width placement.
     */
    compact?: boolean;
}

export function WizardStepIndicator({ current, compact = false }: WizardStepIndicatorProps) {
    const currentIdx = STEP_ORDER.indexOf(current);

    return (
        <ol
            className={cn('flex items-center', compact ? 'gap-2.5' : 'w-full justify-center gap-4')}
            aria-label="Create case progress"
        >
            {STEP_ORDER.map((step, idx) => {
                const state: 'completed' | 'active' | 'upcoming' =
                    idx < currentIdx ? 'completed' : idx === currentIdx ? 'active' : 'upcoming';
                return (
                    <li key={step} className={cn('flex items-center', compact ? 'gap-1.5' : 'gap-3')}>
                        <span
                            className={cn(
                                'flex items-center justify-center rounded-full border font-medium',
                                compact ? 'h-5 w-5 text-[10px]' : 'h-7 w-7 text-xs',
                                state === 'completed' && 'border-primary bg-primary text-primary-foreground',
                                state === 'active' && 'border-primary text-primary',
                                state === 'upcoming' && 'border-muted-foreground/30 text-muted-foreground'
                            )}
                            aria-current={state === 'active' ? 'step' : undefined}
                        >
                            {state === 'completed' ? (
                                <Check className={compact ? 'h-3 w-3' : 'h-4 w-4'} />
                            ) : (
                                idx + 1
                            )}
                        </span>
                        <span
                            className={cn(
                                compact ? 'text-xs' : 'text-sm',
                                state === 'active' && 'font-medium text-foreground',
                                state !== 'active' && 'text-muted-foreground'
                            )}
                        >
                            {STEP_LABELS[step]}
                        </span>
                        {idx < STEP_ORDER.length - 1 && (
                            <span
                                className={cn(
                                    'h-px bg-muted-foreground/30',
                                    compact ? 'ml-1 w-6' : 'ml-2 w-12'
                                )}
                                aria-hidden
                            />
                        )}
                    </li>
                );
            })}
        </ol>
    );
}

export { STEP_ORDER, STEP_LABELS };
